'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { VoucherPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/VoucherPriceFragmentFragmentDoc';
import { Box, Typography } from '@packages/shared';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { extractQueryParameter } from '../../helpers/extractQueryParameter';
import { usePriceFormater } from '../../hooks/usePriceFormater';
import { voucherPriceMessages } from './messages';

/* GraphQL */ `
  fragment VoucherPriceFragment on ProductPrice {
    currency
    value
  }
`;

export type VoucherPriceProps = {
  data: FragmentType<typeof VoucherPriceFragmentFragmentDoc>;
};

/**
 * <p>This component renders a voucher price, also called "Geizhals Einsprung".</p>
 * */
export const VoucherPrice = ({ data }: VoucherPriceProps) => {
  const { value, currency } = getFragmentData(VoucherPriceFragmentFragmentDoc, data);
  const searchParams = useSearchParams();
  const { voucherPriceDiscount } = extractQueryParameter(
    searchParams ?? new ReadonlyURLSearchParams(new URLSearchParams()),
  );
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('PRODUCT');

  if (!voucherPriceDiscount) {
    return null;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          color: 'sale.main',
          fontWeight: 'fontWeightBold',
        }}
      >
        {formatMessage(voucherPriceMessages.DiscountPriceText, {
          price: formatPrice((value * ((100 - voucherPriceDiscount) / 100)) / 100, currency),
        })}
      </Typography>
      <Typography
        sx={{
          typography: 'body3',
          color: 'text.darkTransparent',
        }}
      >
        {formatMessage(voucherPriceMessages.DiscountSubtext)}
      </Typography>
    </Box>
  );
};
